import { useCallback, useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { FileText, Download, Eye, Trash2, AlertTriangle } from 'lucide-react';
import { cpdAPI, errMsg, download, ACT_STATUS } from '../../lib/cpdRegisterAPI';
import { formatAUDate } from '../../lib/dateUtils';
import { toast } from 'sonner';

export const CPDActivityDrawer = ({ id, me, onClose, onEdit, onChanged }) => {
  const [a, setA] = useState(null);
  const [reason, setReason] = useState('');
  const load = useCallback(() => id && cpdAPI.activity(id).then(setA).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { setA(null); setReason(''); load(); }, [load]);
  const act = async (action) => {
    try { await cpdAPI.action(id, action, reason); toast.success('Done'); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm('Delete this CPD activity?')) return;
    try { await cpdAPI.remove(id); toast.success('Deleted'); onClose(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  const open = async (ev, inline) => {
    try {
      const b = await cpdAPI.blob(ev.id, inline);
      if (inline) window.open(URL.createObjectURL(b), '_blank'); else download(b, ev.filename);
    } catch (e) { toast.error(errMsg(e, 'Could not open file')); }
  };
  const reviewer = ['admin', 'manager'].includes(me.tier) && a && (a.user_id !== me.user_id || me.tier === 'admin');
  return (
    <Sheet open={!!id} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="cpd-activity-drawer">
        {!a ? <p className="text-sm text-muted-foreground mt-10">Loading…</p> : (
          <div className="space-y-5">
            <SheetHeader><SheetTitle>{a.title}</SheetTitle></SheetHeader>
            <div className="flex gap-2 flex-wrap"><Badge variant="outline" data-testid="cpd-drawer-status">{ACT_STATUS[a.status]}</Badge>
              <Badge variant="outline" data-testid="cpd-drawer-evidence">Evidence: {a.evidence_status}</Badge></div>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-muted-foreground">Date</dt><dd>{formatAUDate(a.date)}</dd>
              <dt className="text-muted-foreground">Provider</dt><dd>{a.provider || '—'}</dd>
              <dt className="text-muted-foreground">Hours</dt><dd>{a.hours}</dd>
              <dt className="text-muted-foreground">Type</dt><dd>{a.activity_type}</dd>
              <dt className="text-muted-foreground">Evidence type</dt><dd>{a.evidence_type}</dd>
              <dt className="text-muted-foreground">Delivery</dt><dd>{a.delivery_method || '—'}</dd>
              <dt className="text-muted-foreground">Categories</dt><dd>{Object.entries(a.category_hours).map(([c, h]) => `${c} ${h}h`).join(', ')}</dd>
              {a.credit_group && <><dt className="text-muted-foreground">CPD group</dt><dd>{a.credit_group}</dd></>}
              {Object.keys(a.area_hours || {}).length > 0 && <><dt className="text-muted-foreground">Areas</dt><dd>{Object.entries(a.area_hours).map(([c, h]) => `${c} ${h}h`).join(', ')}</dd></>}
            </dl>
            {a.description && <p className="text-sm whitespace-pre-wrap">{a.description}</p>}
            {a.record_warnings?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1" data-testid="cpd-record-warnings">
                {a.record_warnings.map((w) => <p key={w} className="flex gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{w}</p>)}</div>
            )}
            <div className="space-y-2"><p className="text-sm font-semibold">Evidence</p>
              {a.evidence.length === 0 && <p className="text-sm text-muted-foreground">No files.</p>}
              {a.evidence.map((ev) => (
                <div key={ev.id} className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm" data-testid={`cpd-evidence-${ev.id}`}>
                  <FileText className="w-4 h-4 text-slate-500" /><span className="flex-1 truncate">{ev.filename}<span className="block text-xs text-muted-foreground">{ev.doc_category} · {ev.scan_status}</span></span>
                  <Button size="icon" variant="ghost" onClick={() => open(ev, true)} data-testid={`cpd-evidence-preview-${ev.id}`}><Eye className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => open(ev, false)} data-testid={`cpd-evidence-download-${ev.id}`}><Download className="w-4 h-4" /></Button>
                </div>))}
            </div>
            {reviewer && a.status === 'submitted' && (
              <div className="space-y-2 rounded-xl border p-3" data-testid="cpd-review-panel"><p className="text-sm font-semibold">Review</p>
                <Textarea placeholder="Reason (required to reject or request changes)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cpd-review-reason" />
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => act('approve')} data-testid="cpd-approve-btn">Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => act('request_changes')} data-testid="cpd-request-changes-btn">Request changes</Button>
                  <Button size="sm" variant="outline" className="text-rose-700" onClick={() => act('reject')} data-testid="cpd-reject-btn">Reject</Button>
                </div></div>
            )}
            <div className="space-y-1"><p className="text-sm font-semibold">History</p>
              {(a.history || []).map((h, i) => <p key={i} className="text-xs text-muted-foreground">{formatAUDate(h.at)} · {h.from ? `${ACT_STATUS[h.from] || h.from} → ` : ''}{ACT_STATUS[h.to] || h.to}{h.reason ? ` – ${h.reason}` : ''}</p>)}</div>
            {me.tier !== 'auditor' && (
              <div className="flex gap-2">
                {['draft', 'changes_requested'].includes(a.status) && a.user_id === me.user_id && <Button size="sm" onClick={() => act('submit')} data-testid="cpd-submit-btn">Submit for approval</Button>}
                <Button size="sm" variant="outline" onClick={() => onEdit(a)} data-testid="cpd-edit-btn">Edit</Button>
                <Button size="sm" variant="ghost" className="text-rose-700" onClick={del} data-testid="cpd-delete-btn"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
